import React from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import { reduxForm, Field, FieldArray } from 'redux-form';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { LoadingSpinner } from '../LoadingSpinner';
import {
    Container as DefaultContainer,
    Label,
    MultiChoiceGroup,
    MultipleGroupFull,
    MultipleGroupHalf,
    MultipleGroupVolunteerWithCountry
} from '../../containers/DefaultStyles/FormDetails';

import Button from '../Button';
import CheckboxGroup from '../CheckboxGroup';
import DropDownSelect from '../DropDownSelect';
import InputGroup from '../InputGroup';
import TextareaGroup from '../TextareaGroup';
import TwoGroup from '../TwoGroup';
import {
    ButtonGroupWrapper,
    OnSubmitValidator
} from '../../containers/DefaultStyles/FormDetails';

import { CountryCodes, VolunteerQuestionTypes } from '../../utils/enums';
import { getCachedFirstName, getCachedLastName, findCachedValueByActionIdentifier } from '../../utils/formCaching/helpers';
import { isPortrait } from '../../utils/size';
import { styledScrollbar } from '../Shared/mixins';
import { requiredField } from '../Shared/validations';
import { validate } from './validations';
import submissionErrorHandler from '../../utils/submissionErrorHandler';
import { ThemeConsumer } from '../../utils/theming';

import { copyFor, capitalization } from '../../copy/copy';

const Container = styled(DefaultContainer)`
    margin-top: 12px;
`;

const TermsAndConditions = styled.div`
    width: 33.333%;
    ${() =>
        isPortrait()
            ? ` width: 100%;
                margin: 20px 0;`
            : null}
    > div {
        padding: 0;
    }
    label {
        font-size: 8px;
        line-height: 1.25;
        font-weight: normal;
        letter-spacing: normal;
        a {
            color: #008eff;
            text-decoration: none;
        }
    }
`;

const groupQuestions = (questions, n) => {
    return questions
        .map((q, i) => {
            if (i % n === 0) {
                return questions.slice(i, i + n);
            } else {
                return null;
            }
        })
        .filter(q => q);
};

const MultiChoice = ({ fields, data }) => (
    <div>
        {data.choices.map((f, i) => {
            return (
                <Field
                    key={i}
                    name={f}
                    type="radio"
                    keepExistingCapitalization={true}
                    component={CheckboxGroup}
                    label={f}
                    onClick={() => {
                        (fields.getAll() || []).includes(f)
                            ? fields.remove(i)
                            : fields.push(f);
                    }}
                />
            );
        })}
    </div>
);

MultiChoice.propTypes = {
    fields: PropTypes.array,
    data: PropTypes.array
};

const StickyButtonGroupWrapper = styled(ButtonGroupWrapper)`
    position: sticky;
    background-color: #ffffff;
    padding: 0;
    margin: 0;
    margin-top: ${({ noFooter }) => (isPortrait() ? 0 : noFooter ? 16 : 20)}px;
    width: 100%;
    bottom: 0px;
    background-color: white;
    ${() => isPortrait() && `flex-direction: column;`};
    @media (max-width: 30em) {
        bottom: -1px;
        padding-bottom: 20px;
    }
`;

const tosLink = url => `<a href=${url} target="_blank" rel="noopener noreferrer"/>`;

const legalInfo = copyFor('miscellaneous', 'legalInfo', 'volunteer', undefined, {
    termsLink: tosLink('https://www.actionbutton.org/terms'),
    endTermsLink: '</a>',
    privacyPolicyLink: tosLink('https://www.actionbutton.org/privacy'),
    endPrivacyPolicyLink: '</a>',
    VMTermsLink: tosLink('https://www.volunteermatch.org/legal/terms.jsp'),
    endVMTermsLink: '</a>'
});

const TOS = (
    <span
        dangerouslySetInnerHTML={{
            __html: legalInfo
        }}
    />
);

const GroupWrapper = styled.div`
    overflow-y: scroll;
    overflow-x: hidden;
    height: 150px;
    ${({ theme }) => styledScrollbar(theme.colors.primary)};
    padding-right: 10px;
`;

class VolunteerForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            valid: false,
            errors: submissionErrorHandler(props.currentFormValues, validate)
        };
        this.clearSubmitErrors = this.clearSubmitErrors.bind(this);
    }

    componentDidMount() {
        this.setState({ errors: [] });
    }

    setSubmitErrors(errors) {
        this.setState({ errors });
    }

    checkForBlurError(name) {
        const errors = submissionErrorHandler(this.props.currentFormValues, validate);
        if (errors.includes(name) && !this.state.errors.includes(name))
            this.setState({ errors: [...this.state.errors, name] });
    }

    clearSubmitErrors(fieldName) {
        if (fieldName && this.state.errors.includes(fieldName)) {
            this.setState({ errors: this.state.errors.filter(e => e !== fieldName) });
        }
    }

    render() {
        return (
            <ThemeConsumer>
                {theme => (
                    <Container
                        autocomplete="on"
                        onSubmit={this.props.handleSubmit}
                        className="ie-volunteer-form-container"
                    >
                        {isPortrait() ? (
                            <div>
                                <MultipleGroupFull>
                                    <Field
                                        name="firstName"
                                        onBlur={() => this.checkForBlurError('firstName')}
                                        submissionError={this.state.errors.includes(
                                            'firstName'
                                        )}
                                        clearSubmitErrors={this.clearSubmitErrors}
                                        component={InputGroup}
                                        type="text"
                                        label={copyFor(
                                            'formFields',
                                            'firstName',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        placeholder={copyFor(
                                            'placeholders',
                                            'firstName',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        autocomplete="on"
                                    />
                                    <Field
                                        name="lastName"
                                        onBlur={() => this.checkForBlurError('lastName')}
                                        submissionError={this.state.errors.includes(
                                            'lastName'
                                        )}
                                        clearSubmitErrors={this.clearSubmitErrors}
                                        component={InputGroup}
                                        type="text"
                                        label={copyFor(
                                            'formFields',
                                            'lastName',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        placeholder={copyFor(
                                            'placeholders',
                                            'lastName',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        autocomplete="on"
                                    />
                                </MultipleGroupFull>
                                <MultipleGroupFull>
                                    <Field
                                        name="email"
                                        onBlur={() => this.checkForBlurError('email')}
                                        submissionError={this.state.errors.includes(
                                            'email'
                                        )}
                                        clearSubmitErrors={this.clearSubmitErrors}
                                        component={InputGroup}
                                        type="email"
                                        label={copyFor(
                                            'formFields',
                                            'emailAddress',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        placeholder={copyFor(
                                            'placeholders',
                                            'emailAddress',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        autocomplete="on"
                                    />
                                </MultipleGroupFull>
                                <MultipleGroupFull>
                                    <Field
                                        name="phone"
                                        onBlur={() => this.checkForBlurError('phone')}
                                        submissionError={this.state.errors.includes(
                                            'phone'
                                        )}
                                        clearSubmitErrors={this.clearSubmitErrors}
                                        component={InputGroup}
                                        type="text"
                                        label={copyFor(
                                            'formFields',
                                            'phone',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        placeholder={copyFor(
                                            'placeholders',
                                            'phone',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        autocomplete="on"
                                    />
                                    <Field
                                        name="postalCode"
                                        onBlur={() =>
                                            this.checkForBlurError('postalCode')
                                        }
                                        submissionError={this.state.errors.includes(
                                            'postalCode'
                                        )}
                                        clearSubmitErrors={this.clearSubmitErrors}
                                        component={InputGroup}
                                        type="text"
                                        label={copyFor(
                                            'formFields',
                                            'postalCode',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        placeholder={copyFor(
                                            'placeholders',
                                            'postalCode',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        autocomplete="on"
                                        maxLength={5}
                                    />
                                </MultipleGroupFull>
                                <MultipleGroupFull>
                                    <Field
                                        name="countryCode"
                                        onBlur={() =>
                                            this.checkForBlurError('countryCode')
                                        }
                                        submissionError={this.state.errors.includes(
                                            'countryCode'
                                        )}
                                        clearSubmitErrors={this.clearSubmitErrors}
                                        countryCodeDropdown={true}
                                        component={DropDownSelect}
                                        type="text"
                                        label={copyFor(
                                            'formFields',
                                            'country',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        options={CountryCodes}
                                    />
                                </MultipleGroupFull>
                                {groupQuestions(
                                    this.props.singleChoiceQuestions,
                                    isPortrait() ? 2 : 3
                                ).map((qGroup, i) => (
                                    <MultipleGroupHalf key={i}>
                                        {qGroup.map((q, j) => (
                                            <Field
                                                key={j}
                                                name={`question|${q.id}`}
                                                component={DropDownSelect}
                                                options={q.choices}
                                                defaultOption={'Choose'}
                                                validate={
                                                    q.required ? [requiredField] : []
                                                }
                                                label={q.label}
                                                type="text"
                                            />
                                        ))}
                                    </MultipleGroupHalf>
                                ))}

                                {groupQuestions(
                                    this.props.multiChoiceQuestions,
                                    isPortrait() ? 2 : 3
                                ).map((qGroup, i) => (
                                    <MultipleGroupHalf key={i}>
                                        {qGroup.map((q, j) => (
                                            <MultiChoiceGroup key={j}>
                                                <Label>{q.label}</Label>
                                                <FieldArray
                                                    name={`question|${q.id}`}
                                                    data={q}
                                                    validate={
                                                        q.required ? [requiredField] : []
                                                    }
                                                    component={MultiChoice}
                                                />
                                            </MultiChoiceGroup>
                                        ))}
                                    </MultipleGroupHalf>
                                ))}

                                {groupQuestions(
                                    this.props.singleLineQuestions,
                                    isPortrait() ? 2 : 3
                                ).map((qGroup, i) => (
                                    <MultipleGroupHalf key={i}>
                                        {qGroup.map((q, j) => (
                                            <MultiChoiceGroup key={j}>
                                                <Field
                                                    key={j}
                                                    name={`question|${q.id}`}
                                                    component={InputGroup}
                                                    type="text"
                                                    label={q.label}
                                                    validate={
                                                        q.required ? [requiredField] : []
                                                    }
                                                    autocomplete="off"
                                                />
                                            </MultiChoiceGroup>
                                        ))}
                                    </MultipleGroupHalf>
                                ))}

                                {groupQuestions(this.props.multiLineQuestions, 1).map(
                                    (qGroup, i) => (
                                        <MultipleGroupFull key={i}>
                                            {qGroup.map((q, j) => (
                                                <Field
                                                    key={j}
                                                    label={q.label}
                                                    name={`question|${q.id}`}
                                                    validate={
                                                        q.required ? [requiredField] : []
                                                    }
                                                    component={TextareaGroup}
                                                    placeholder={
                                                        q.required
                                                            ? copyFor(
                                                                  'placeholders',
                                                                  'required',
                                                                  'volunteer'
                                                              )
                                                            : ''
                                                    }
                                                    readOnly={false}
                                                    class="volunteer"
                                                    classLabel="volunteer"
                                                />
                                            ))}
                                        </MultipleGroupFull>
                                    )
                                )}
                                <MultipleGroupHalf>
                                    <Field
                                        title="message"
                                        name="message"
                                        component={TextareaGroup}
                                        label={copyFor(
                                            'formFields',
                                            'message',
                                            'volunteer'
                                        )}
                                        placeholder={copyFor(
                                            'placeholders',
                                            'message',
                                            'volunteer',
                                            null,
                                            {
                                                organizationName: this.props
                                                    .volunteerOpportunity.parentOrg.name
                                            }
                                        )}
                                        class="volunteer"
                                        classLabel="volunteer"
                                        readOnly={false}
                                    />
                                </MultipleGroupHalf>
                            </div>
                        ) : (
                            <GroupWrapper theme={theme}>
                                <MultipleGroupFull>
                                    <Field
                                        name="firstName"
                                        onBlur={() => this.checkForBlurError('firstName')}
                                        submissionError={this.state.errors.includes(
                                            'firstName'
                                        )}
                                        clearSubmitErrors={this.clearSubmitErrors}
                                        component={InputGroup}
                                        type="text"
                                        label={copyFor(
                                            'formFields',
                                            'firstName',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        placeholder={copyFor(
                                            'placeholders',
                                            'firstName',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        autocomplete="on"
                                    />
                                    <Field
                                        name="lastName"
                                        onBlur={() => this.checkForBlurError('lastName')}
                                        submissionError={this.state.errors.includes(
                                            'lastName'
                                        )}
                                        clearSubmitErrors={this.clearSubmitErrors}
                                        component={InputGroup}
                                        type="text"
                                        label={copyFor(
                                            'formFields',
                                            'lastName',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        placeholder={copyFor(
                                            'placeholders',
                                            'lastName',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        autocomplete="on"
                                    />
                                    <Field
                                        name="email"
                                        onBlur={() => this.checkForBlurError('email')}
                                        submissionError={this.state.errors.includes(
                                            'email'
                                        )}
                                        clearSubmitErrors={this.clearSubmitErrors}
                                        component={InputGroup}
                                        type="email"
                                        label={copyFor(
                                            'formFields',
                                            'emailAddress',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        placeholder={copyFor(
                                            'placeholders',
                                            'emailAddress',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        autocomplete="on"
                                    />
                                </MultipleGroupFull>
                                <MultipleGroupVolunteerWithCountry>
                                    <Field
                                        name="phone"
                                        onBlur={() => this.checkForBlurError('phone')}
                                        submissionError={this.state.errors.includes(
                                            'phone'
                                        )}
                                        clearSubmitErrors={this.clearSubmitErrors}
                                        component={InputGroup}
                                        type="text"
                                        label={copyFor(
                                            'formFields',
                                            'phone',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        placeholder={copyFor(
                                            'placeholders',
                                            'phone',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        autocomplete="on"
                                    />
                                    <Field
                                        name="postalCode"
                                        onBlur={() =>
                                            this.checkForBlurError('postalCode')
                                        }
                                        submissionError={this.state.errors.includes(
                                            'postalCode'
                                        )}
                                        clearSubmitErrors={this.clearSubmitErrors}
                                        component={InputGroup}
                                        type="text"
                                        label={copyFor(
                                            'formFields',
                                            'postalCode',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        placeholder={copyFor(
                                            'placeholders',
                                            'postalCode',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        autocomplete="on"
                                        maxLength={5}
                                    />
                                    <Field
                                        name="countryCode"
                                        onBlur={() =>
                                            this.checkForBlurError('countryCode')
                                        }
                                        submissionError={this.state.errors.includes(
                                            'countryCode'
                                        )}
                                        clearSubmitErrors={this.clearSubmitErrors}
                                        countryCodeDropdown={true}
                                        component={DropDownSelect}
                                        type="text"
                                        label={copyFor(
                                            'formFields',
                                            'country',
                                            'volunteer',
                                            capitalization.eachWord
                                        )}
                                        options={CountryCodes}
                                    />
                                </MultipleGroupVolunteerWithCountry>
                                {groupQuestions(
                                    this.props.singleChoiceQuestions,
                                    isPortrait() ? 2 : 3
                                ).map((qGroup, i) => (
                                    <MultipleGroupHalf key={i}>
                                        {qGroup.map((q, j) => (
                                            <Field
                                                key={j}
                                                name={`question|${q.id}`}
                                                component={DropDownSelect}
                                                options={q.choices}
                                                defaultOption={'Choose'}
                                                validate={
                                                    q.required ? [requiredField] : []
                                                }
                                                label={q.label}
                                                type="text"
                                            />
                                        ))}
                                    </MultipleGroupHalf>
                                ))}

                                {groupQuestions(
                                    this.props.multiChoiceQuestions,
                                    isPortrait() ? 2 : 3
                                ).map((qGroup, i) => (
                                    <MultipleGroupHalf key={i}>
                                        {qGroup.map((q, j) => (
                                            <MultiChoiceGroup key={j}>
                                                <Label>{q.label}</Label>
                                                <FieldArray
                                                    name={`question|${q.id}`}
                                                    data={q}
                                                    validate={
                                                        q.required ? [requiredField] : []
                                                    }
                                                    component={MultiChoice}
                                                />
                                            </MultiChoiceGroup>
                                        ))}
                                    </MultipleGroupHalf>
                                ))}

                                {groupQuestions(
                                    this.props.singleLineQuestions,
                                    isPortrait() ? 2 : 3
                                ).map((qGroup, i) => (
                                    <MultipleGroupHalf key={i}>
                                        {qGroup.map((q, j) => (
                                            <MultiChoiceGroup key={j}>
                                                <Field
                                                    key={j}
                                                    name={`question|${q.id}`}
                                                    component={InputGroup}
                                                    type="text"
                                                    label={q.label}
                                                    validate={
                                                        q.required ? [requiredField] : []
                                                    }
                                                    autocomplete="off"
                                                />
                                            </MultiChoiceGroup>
                                        ))}
                                    </MultipleGroupHalf>
                                ))}

                                {groupQuestions(this.props.multiLineQuestions, 1).map(
                                    (qGroup, i) => (
                                        <MultipleGroupFull key={i}>
                                            {qGroup.map((q, j) => (
                                                <Field
                                                    key={j}
                                                    label={q.label}
                                                    name={`question|${q.id}`}
                                                    validate={
                                                        q.required ? [requiredField] : []
                                                    }
                                                    component={TextareaGroup}
                                                    placeholder={
                                                        q.required
                                                            ? copyFor(
                                                                  'placeholders',
                                                                  'required',
                                                                  'volunteer'
                                                              )
                                                            : ''
                                                    }
                                                    readOnly={false}
                                                    class="volunteer"
                                                    classLabel="volunteer"
                                                />
                                            ))}
                                        </MultipleGroupFull>
                                    )
                                )}
                                <MultipleGroupHalf>
                                    <Field
                                        title="message"
                                        name="message"
                                        component={TextareaGroup}
                                        label={copyFor(
                                            'formFields',
                                            'message',
                                            'volunteer'
                                        )}
                                        placeholder={copyFor(
                                            'placeholders',
                                            'message',
                                            'volunteer',
                                            null,
                                            {
                                                organizationName: this.props
                                                    .volunteerOpportunity.parentOrg.name
                                            }
                                        )}
                                        class="volunteer"
                                        classLabel="volunteer"
                                        readOnly={false}
                                    />
                                </MultipleGroupHalf>
                            </GroupWrapper>
                        )}
                        <StickyButtonGroupWrapper
                            noFooter
                            isSubmitting={this.props.isSubmitting}
                        >
                            <TermsAndConditions>
                                <Field
                                    name="tos"
                                    label=""
                                    id="tos"
                                    keepExistingCapitalization={true}
                                    component={CheckboxGroup}
                                    link={TOS}
                                    validate={[requiredField]}
                                />
                            </TermsAndConditions>
                            <TwoGroup
                                style={{
                                    paddingLeft: isPortrait() ? `0px` : '16px',
                                    width: isPortrait() ? `100%` : '66.666%'
                                }}
                            >
                                <Button label="Back" onClick={this.props.onBack} />
                                <OnSubmitValidator
                                    onClick={e => {
                                        e.preventDefault();
                                        const errors = submissionErrorHandler(
                                            this.props.currentFormValues,
                                            validate
                                        );
                                        if (errors.length === 0 && !this.props.invalid) {
                                            this.props.handleSubmit();
                                        } else {
                                            this.setSubmitErrors(errors);
                                        }
                                    }}
                                >
                                <Button
                                    primary={true}
                                    type="submit"
                                    className="submitButton"
                                    label={
                                        this.props.isSubmitting ?
                                        (<LoadingSpinner />)
                                        : copyFor(
                                            'buttons',
                                            'continue',
                                            'volunteer',
                                            capitalization.eachWord
                                        )

                                    }
                                    disabled={this.props.invalid}
                                    isSubmitting={this.props.isSubmitting}
                                />
                                </OnSubmitValidator>
                                <LoadingSpinner />
                            </TwoGroup>
                        </StickyButtonGroupWrapper>
                    </Container>
                )}
            </ThemeConsumer>
        );
    }
}

VolunteerForm.propTypes = {
    currentFormValues: PropTypes.object,
    onBack: PropTypes.func,
    invalid: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    singleChoiceQuestions: PropTypes.array,
    multiChoiceQuestions: PropTypes.array,
    singleLineQuestions: PropTypes.array,
    multiLineQuestions: PropTypes.array,
    volunteerOpportunity: PropTypes.object.isRequired
};

export default compose(
    connect((state, props) => {
        const questions = props.volunteerOpportunity.questions;
        const actionIdentifier = state.widget.activeAction.identifier;

        return {
            initialValues: {
                firstName: getCachedFirstName(state),
                lastName: getCachedLastName(state),
                email: state.cachedFormData.email || '',
                phone: state.cachedFormData.phone || '',
                postalCode: state.widget.endUserLocation
                    ? state.widget.endUserLocation.postalCode
                    : '',
                countryCode: state.widget.endUserLocation.countryCode
                    ? state.widget.endUserLocation.countryCode
                    : 'US',
                tos: !(state.widget.endUserLocation || {}).isEU,
                message: findCachedValueByActionIdentifier(
                            state.cachedFormData.message,
                            actionIdentifier
                        )
            },

            currentFormValues: (state.form['volunteer'] || {}).values,
            isSubmitting: state.volunteer.isSubmitting,
            multiChoiceQuestions: questions.filter(
                q => q.type === VolunteerQuestionTypes.multiChoice
            ),
            multiLineQuestions: questions.filter(
                q => q.type === VolunteerQuestionTypes.multiLine
            ),
            singleChoiceQuestions: questions.filter(
                q => q.type === VolunteerQuestionTypes.singleChoice
            ),
            singleLineQuestions: questions.filter(
                q => q.type === VolunteerQuestionTypes.singleLine
            ),
            volunteerOpportunity: props.volunteerOpportunity
        };
    }),
    reduxForm({
        form: 'volunteer',
        destroyOnUnmount: false,
        validate
    })
)(VolunteerForm);
